import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ArrowRight, Loader2, Calculator } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QuickQuote = () => {
  const navigate = useNavigate();
  const [dims, setDims] = useState({ length: '', width: '', height: '' });
  const [quantity, setQuantity] = useState('100');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const canQuote = dims.length && dims.width && dims.height && quantity;

  const handleQuote = useCallback(async () => {
    if (!canQuote) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/quote`, {
        dimensions: {
          length: parseFloat(dims.length) * 10,
          width: parseFloat(dims.width) * 10,
          height: parseFloat(dims.height) * 10,
        },
        quantity: parseInt(quantity, 10) || 1,
        has_printing: false,
        coverage_percent: 0,
      });
      setResult(res.data);
    } catch {
      setError('Verifica las medidas e intenta de nuevo');
    }
    setLoading(false);
  }, [dims, quantity, canQuote]);

  const goToConfigurator = () => {
    const params = new URLSearchParams({
      length: (parseFloat(dims.length) * 10).toString(),
      width: (parseFloat(dims.width) * 10).toString(),
      height: (parseFloat(dims.height) * 10).toString(),
      quantity: quantity,
    });
    navigate(`/configurar?${params.toString()}`);
  };

  return (
    <section className="py-16 lg:py-24 bg-white border-t border-b border-border/30" data-testid="quick-quote-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-accent/10 text-accent px-4 py-1.5 rounded-full text-sm font-medium mb-4">
            <Calculator className="w-4 h-4" />
            Cotizador express
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-primary mb-3">
            Cotiza en segundos
          </h2>
          <p className="text-muted-foreground max-w-lg mx-auto">
            Ingresa las medidas de tu caja y obtén un precio estimado al instante
          </p>
        </div>

        <div className="bg-muted/20 border border-border/50 rounded-2xl p-6 sm:p-8">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1.5">Largo (cm)</label>
              <Input
                type="number"
                placeholder="Ej: 40"
                value={dims.length}
                onChange={(e) => { setDims(p => ({ ...p, length: e.target.value })); setResult(null); }}
                className="h-12 text-center text-lg font-semibold"
                min="1"
                data-testid="quick-length"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1.5">Ancho (cm)</label>
              <Input
                type="number"
                placeholder="Ej: 30"
                value={dims.width}
                onChange={(e) => { setDims(p => ({ ...p, width: e.target.value })); setResult(null); }}
                className="h-12 text-center text-lg font-semibold"
                min="1"
                data-testid="quick-width"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1.5">Alto (cm)</label>
              <Input
                type="number"
                placeholder="Ej: 20"
                value={dims.height}
                onChange={(e) => { setDims(p => ({ ...p, height: e.target.value })); setResult(null); }}
                className="h-12 text-center text-lg font-semibold"
                min="1"
                data-testid="quick-height"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1.5">Cantidad</label>
              <Input
                type="number"
                placeholder="100"
                value={quantity}
                onChange={(e) => { setQuantity(e.target.value); setResult(null); }}
                className="h-12 text-center text-lg font-semibold"
                min="1"
                data-testid="quick-quantity"
              />
            </div>
          </div>

          <div className="flex justify-center mb-6">
            <Button
              onClick={handleQuote}
              disabled={!canQuote || loading}
              className="h-12 px-10 bg-accent hover:bg-accent/90 text-white rounded-full text-base gap-2"
              data-testid="quick-quote-btn"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Calculator className="w-5 h-5" />}
              Cotizar
            </Button>
          </div>

          {error && (
            <p className="text-center text-sm text-red-500 mb-4" data-testid="quick-quote-error">{error}</p>
          )}

          {result && (
            <div className="animate-fadeIn" data-testid="quick-quote-result">
              <div className="bg-white rounded-xl border border-accent/20 p-6 space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="text-xs text-muted-foreground">Material</p>
                    <p className="font-semibold text-primary text-sm">{result.material_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Precio unitario</p>
                    <p className="font-bold text-primary text-lg">${result.unit_price_with_tax?.toFixed(2)}</p>
                    <p className="text-[10px] text-muted-foreground">IVA incluido</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{quantity} {parseInt(quantity) === 1 ? 'caja' : 'cajas'}</p>
                    <p className="font-bold text-accent text-xl">${result.total_price_with_tax?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                    <p className="text-[10px] text-muted-foreground">Total con IVA</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Sin impresión</p>
                    <p className="text-xs text-muted-foreground mt-1">Agrega impresión en el configurador</p>
                  </div>
                </div>

                <div className="flex justify-center pt-2">
                  <Button
                    onClick={goToConfigurator}
                    className="h-11 px-8 bg-primary hover:bg-primary/90 text-white rounded-full gap-2"
                    data-testid="quick-go-configurator"
                  >
                    Personalizar y ordenar
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-center text-[11px] text-muted-foreground">
                  Precio estimado con material {result.material_name}. Puede variar según material e impresión.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default QuickQuote;
